// Natural lake and ravine materials (Oct 14 2026; Valor/output/terrain-sprites-natural-v1, CLAUDE-HANDOFF.md). Four
// opaque painted swatches give the lakes and gorges a surface — the water, the shore's wet gravel, the gorge walls' rock
// and its floor — and two NW-lit transparent accents replace the drawn lake stones and the gorge-lip boulder. The
// renderer's own fields still decide where water and gorges are: a material only varies the RGB of a sample the field
// already painted, never its alpha.
//
// Sampling (the manifest's contract): in world cells, x east and y north, one swatch spans MATERIAL_SPAN cells and every
// next swatch is mirrored, so the full period is MATERIAL_PERIOD cells (which divides every world size, 256–2048, so the
// world wrap is seamless too). Mirroring joins identical source edges, so the PNGs' own opposite edges needn't match. A
// material is read back once into a READBACK² buffer; nothing here runs in the animation loop.
import { art } from "@/kit/art";

export const MATERIAL_SPAN = 2;
export const MATERIAL_PERIOD = 2 * MATERIAL_SPAN;
/** the manifest's textureReadbackSizePx */
export const READBACK = 256;

/** A material's sampling buffer (RGBA, READBACK²) and the source's mean colour (the neutral reference). */
export interface Material {
  w: number;
  h: number;
  data: Uint8ClampedArray;
  mean: readonly [number, number, number];
}

/** An accent sprite: its image, the contact anchor and painted bounds (source px, from the manifest). */
export interface Accent {
  im: HTMLImageElement;
  /** bottom centre of the painted (alpha > 24) bounds */
  anchor: readonly [number, number];
  /** [left, top, right, bottom) */
  bounds: readonly [number, number, number, number];
}

export interface TerrainMaterials {
  lakeWater: Material | null;
  lakeShore: Material | null;
  ravineRock: Material | null;
  ravineFloor: Material | null;
  lakeStone: Accent | null;
  ravineRimRock: Accent | null;
}

/** From art-src/terrain-natural-v1/asset-manifest.json (tests/unit/terrainNatural.test.ts holds these to it). */
export const MANIFEST = {
  lakeWater: { mean: [46.9113, 80.1997, 89.8365] },
  lakeShore: { mean: [107.8791, 93.269, 61.1229] },
  ravineRock: { mean: [114.5547, 99.3439, 75.9269] },
  ravineFloor: { mean: [55.4821, 46.6133, 35.4529] },
  lakeStone: { anchor: [639.0, 987], bounds: [134, 338, 1144, 988] },
  ravineRimRock: { anchor: [655.5, 944], bounds: [113, 397, 1198, 945] },
} as const;

export const noMaterials = (): TerrainMaterials => ({ lakeWater: null, lakeShore: null, ravineRock: null, ravineFloor: null, lakeStone: null, ravineRimRock: null });

/** One world axis to a mirrored texture coordinate 0–1: a = posmod(q, 4) / 2, then a ≤ 1 ? a : 2 − a. */
export function mirrorUV(q: number): number {
  let a = q % MATERIAL_PERIOD;
  if (a < 0) a += MATERIAL_PERIOD;
  a /= MATERIAL_SPAN;
  return a <= 1 ? a : 2 - a;
}

/** The material's colour minus its mean at a world point (cells, x east / y north), bilinear: the variation to add to a
 *  procedural colour. Written into `out` [dr, dg, db]. Image v runs south, so it follows −y. */
export function materialDelta(m: Material, wx: number, wy: number, out: number[]): number[] {
  const fx = mirrorUV(wx) * (m.w - 1);
  const fy = mirrorUV(-wy) * (m.h - 1);
  const x0 = Math.min(m.w - 2, Math.floor(fx));
  const y0 = Math.min(m.h - 2, Math.floor(fy));
  const tx = fx - x0;
  const ty = fy - y0;
  const d = m.data;
  const o00 = (y0 * m.w + x0) * 4;
  const o10 = o00 + 4;
  const o01 = o00 + m.w * 4;
  const o11 = o01 + 4;
  for (let c = 0; c < 3; c++) {
    const top = d[o00 + c] + (d[o10 + c] - d[o00 + c]) * tx;
    const bot = d[o01 + c] + (d[o11 + c] - d[o01 + c]) * tx;
    out[c] = top + (bot - top) * ty - m.mean[c];
  }
  return out;
}

/** Draw an accent with its painted width = `paintedW` px and its contact anchor at (footX, footY). */
export function accentRect(a: Accent, paintedW: number, footX: number, footY: number): { x: number; y: number; w: number; h: number } {
  const scale = paintedW / (a.bounds[2] - a.bounds[0]);
  return { x: footX - a.anchor[0] * scale, y: footY - a.anchor[1] * scale, w: a.im.width * scale, h: a.im.height * scale };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.decoding = "async";
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error(`image failed: ${src}`));
    im.src = src;
  });
}

/** Read a material back once: the whole swatch, smoothly downsampled, into a READBACK² buffer. */
export function readBack(im: HTMLImageElement, mean: readonly [number, number, number]): Material | null {
  try {
    // halve in steps first so the downsample averages instead of skipping pixels
    let src: CanvasImageSource = im;
    let sw = im.width;
    const tmp: HTMLCanvasElement[] = [];
    while (sw / 2 >= READBACK * 1.5) {
      const c = document.createElement("canvas");
      c.width = c.height = Math.round(sw / 2);
      const g = c.getContext("2d")!;
      g.imageSmoothingEnabled = true;
      g.imageSmoothingQuality = "high";
      g.drawImage(src, 0, 0, c.width, c.height);
      tmp.push(c);
      src = c;
      sw = c.width;
    }
    const c = document.createElement("canvas");
    c.width = c.height = READBACK;
    const g = c.getContext("2d", { willReadFrequently: true })!;
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = "high";
    g.drawImage(src, 0, 0, READBACK, READBACK);
    const data = g.getImageData(0, 0, READBACK, READBACK).data;
    for (const t of tmp) t.width = t.height = 0;
    c.width = c.height = 0;
    return { w: READBACK, h: READBACK, data, mean };
  } catch {
    return null;
  }
}

let loading: Promise<TerrainMaterials> | null = null;

/** Every material and accent, loaded and read back once per page (each optional: one that fails stays null, and the
 *  renderer keeps its procedural look for it). */
export function loadTerrainMaterials(): Promise<TerrainMaterials> {
  if (loading) return loading;
  const tn = art.map.terrainNatural;
  const mat = (src: string, mean: readonly [number, number, number]) => loadImage(src).then((im) => readBack(im, mean)).catch(() => null);
  const acc = (src: string, a: { anchor: readonly [number, number]; bounds: readonly [number, number, number, number] }) =>
    loadImage(src).then((im): Accent => ({ im, anchor: a.anchor, bounds: a.bounds })).catch(() => null);
  loading = Promise.all([
    mat(tn.lakeWater, MANIFEST.lakeWater.mean),
    mat(tn.lakeShore, MANIFEST.lakeShore.mean),
    mat(tn.ravineRock, MANIFEST.ravineRock.mean),
    mat(tn.ravineFloor, MANIFEST.ravineFloor.mean),
    acc(tn.lakeStone, MANIFEST.lakeStone),
    acc(tn.ravineRimRock, MANIFEST.ravineRimRock),
  ]).then(([lakeWater, lakeShore, ravineRock, ravineFloor, lakeStone, ravineRimRock]) => ({ lakeWater, lakeShore, ravineRock, ravineFloor, lakeStone, ravineRimRock }));
  return loading;
}
